/**
 * Role Service
 * 
 * จัดการ Permissions ตามบทบาท (AdminGroup)
 * - เก็บ permissions ตาม AdminGroup ใน database (Parse Server)
 * - เมื่อแก้ไข permissions ของ Role → admin ทั้งหมดที่มี role นั้นจะได้ permissions ใหม่
 * - แต่ถ้า admin มี permissions ตั้งค่าเอง → ใช้ permissions ของ admin แทน (override)
 */

import { AdminGroup } from '@/domain/admin/admin.types'
import { MENU_PERMISSIONS } from '@/hooks/use-permission'
import { callCloudFunction } from '@/services/adapters/parse/parse-client'

// ============================================
// Types
// ============================================

export interface MenuActions {
  view: boolean
  create: boolean
  edit: boolean
  delete: boolean
  export: boolean
  approve: boolean
}

export interface MenuPermissionConfig {
  path: string
  enabled: boolean
  actions: MenuActions
}

export interface RolePermissions {
  adminGroup: AdminGroup
  permissions: MenuPermissionConfig[] // Array ของ menu permissions พร้อม actions
  updatedAt?: string
  updatedBy?: string
}

// ============================================
// Role Service
// ============================================

export const RoleService = {
  /**
   * ดึง permissions สำหรับ role (AdminGroup) - แบบเก่า (array of paths)
   * ใช้ localStorage เป็น temporary storage (ในอนาคตอาจจะเก็บใน database)
   */
  getRolePermissions(adminGroup: AdminGroup): string[] {
    try {
      const key = `role_permissions_${adminGroup}`
      const stored = localStorage.getItem(key)
      
      if (stored) {
        const parsed = JSON.parse(stored)
        // ถ้าเป็น array เก่า → return ตรงๆ
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') {
          return parsed
        }
        // ถ้าเป็น object ใหม่ → แปลงเป็น array
        if (Array.isArray(parsed) && typeof parsed[0] === 'object') {
          return parsed.filter((p: MenuPermissionConfig) => p.enabled).map((p: MenuPermissionConfig) => p.path)
        }
      }
      
      // ถ้ายังไม่มี → สร้าง default permissions
      return this.getDefaultPermissions(adminGroup)
    } catch (error) {
      console.error('[RoleService] Error getting role permissions:', error)
      return this.getDefaultPermissions(adminGroup)
    }
  },

  /**
   * ดึง permissions แบบใหม่ (พร้อม actions) จาก database
   */
  async getRolePermissionsWithActions(
    sessionToken: string,
    adminGroup: AdminGroup
  ): Promise<MenuPermissionConfig[]> {
    try {
      console.log(`[RoleService] Fetching role permissions for adminGroup ${adminGroup}`)
      
      // ✅ เรียก Cloud Function
      const result = await callCloudFunction(
        'getRolePermissions',
        { admin_group: adminGroup },
        sessionToken
      )

      if (result && result.permissions) {
        console.log(`[RoleService] Got permissions from database:`, result.permissions.length)
        return result.permissions
      }

      // Fallback: สร้าง default permissions
      console.log(`[RoleService] No permissions found, using defaults`)
      return this.getDefaultPermissionsWithActions(adminGroup)
    } catch (error) {
      console.error('[RoleService] Error getting role permissions with actions:', error)
      // Fallback: สร้าง default permissions
      return this.getDefaultPermissionsWithActions(adminGroup)
    }
  },

  /**
   * อัพเดท permissions สำหรับ role (AdminGroup) - แบบเก่า (array of paths)
   */
  updateRolePermissions(adminGroup: AdminGroup, permissions: string[]): void {
    try {
      const key = `role_permissions_${adminGroup}`
      localStorage.setItem(key, JSON.stringify(permissions))
      
      console.log(`[RoleService] Updated permissions for AdminGroup ${adminGroup}:`, permissions)
    } catch (error) {
      console.error('[RoleService] Error updating role permissions:', error)
      throw error
    }
  },

  /**
   * อัพเดท permissions แบบใหม่ (พร้อม actions) ใน database
   */
  async updateRolePermissionsWithActions(
    sessionToken: string,
    adminGroup: AdminGroup,
    permissions: MenuPermissionConfig[]
  ): Promise<void> {
    try {
      console.log(`[RoleService] Updating role permissions for adminGroup ${adminGroup}`)
      
      // ✅ เรียก Cloud Function
      await callCloudFunction(
        'updateRolePermissions',
        {
          admin_group: adminGroup,
          permissions: permissions,
        },
        sessionToken
      )

      // อัพเดทแบบเก่าด้วย (backward compatible - localStorage)
      const paths = permissions.filter(p => p.enabled).map(p => p.path)
      this.updateRolePermissions(adminGroup, paths)
      
      console.log(`[RoleService] Updated permissions with actions for AdminGroup ${adminGroup}`)
    } catch (error) {
      console.error('[RoleService] Error updating role permissions with actions:', error)
      throw error
    }
  },

  /**
   * สร้าง default permissions ตาม admin_group
   */
  getDefaultPermissions(adminGroup: AdminGroup): string[] {
    if (adminGroup === AdminGroup.SUPER_ADMIN) {
      return ['all'] // Super Admin เห็นทุกอย่าง
    }

    // ดึง permissions จาก MENU_PERMISSIONS config
    const allowedPaths: string[] = []
    
    Object.entries(MENU_PERMISSIONS).forEach(([path, allowedGroups]) => {
      if (allowedGroups.includes(adminGroup)) {
        allowedPaths.push(path)
      }
    })

    return allowedPaths
  },

  /**
   * ตรวจสอบว่า permissions ถูกต้องหรือไม่
   */
  validatePermissions(permissions: string[]): boolean {
    if (permissions.length === 0) return false
    if (permissions.includes('all')) return true // Super Admin

    // ตรวจสอบว่า paths ทั้งหมดอยู่ใน MENU_PERMISSIONS หรือไม่
    const validPaths = Object.keys(MENU_PERMISSIONS)
    return permissions.every(path => validPaths.includes(path))
  },

  /**
   * แปลง permissions array เป็น object สำหรับแสดงใน UI
   */
  permissionsToObject(permissions: string[]): Record<string, boolean> {
    const result: Record<string, boolean> = {}
    
    if (permissions.includes('all')) {
      // Super Admin - เปิดทุกอย่าง
      Object.keys(MENU_PERMISSIONS).forEach(path => {
        result[path] = true
      })
      return result
    }

    // ตั้งค่าเฉพาะ paths ที่มีใน permissions
    Object.keys(MENU_PERMISSIONS).forEach(path => {
      result[path] = permissions.includes(path)
    })

    return result
  },

  /**
   * แปลง object เป็น permissions array
   */
  objectToPermissions(obj: Record<string, boolean>): string[] {
    const permissions: string[] = []
    
    Object.entries(obj).forEach(([path, enabled]) => {
      if (enabled) {
        permissions.push(path)
      }
    })

    return permissions
  },

  /**
   * สร้าง default permissions พร้อม actions
   */
  getDefaultPermissionsWithActions(adminGroup: AdminGroup): MenuPermissionConfig[] {
    const allPaths = Object.keys(MENU_PERMISSIONS)
    const defaultPaths = this.getDefaultPermissions(adminGroup)
    const isSuperAdmin = adminGroup === AdminGroup.SUPER_ADMIN
    
    return allPaths.map(path => {
      const enabled = isSuperAdmin || defaultPaths.includes(path)
      return {
        path,
        enabled,
        actions: {
          view: enabled,
          create: enabled && !isSuperAdmin, // Super Admin = true, อื่นๆ = ตาม enabled
          edit: enabled && !isSuperAdmin,
          delete: enabled && !isSuperAdmin,
          export: enabled,
          approve: enabled && !isSuperAdmin,
        },
      }
    })
  },

  /**
   * Reset permissions ของ role กลับเป็น default
   */
  async resetRolePermissions(sessionToken: string, adminGroup: AdminGroup): Promise<void> {
    const defaultPermissions = this.getDefaultPermissions(adminGroup)
    this.updateRolePermissions(adminGroup, defaultPermissions)
    
    // Reset แบบใหม่ด้วย (บันทึกลง database)
    const defaultWithActions = this.getDefaultPermissionsWithActions(adminGroup)
    await this.updateRolePermissionsWithActions(sessionToken, adminGroup, defaultWithActions)
  },
}
